import { getSupabase } from '../../supabaseClient';
import { insertVoluntaryLeaveRow } from '../../utils/groupMemberLeave';

/**
 * Public group join, through join_group (migration 128) - the database decides; the app can no longer insert its
 * own membership. Kept for the chat surface's join gate (GroupDetail); the group page calls join_group directly.
 */
export async function joinPublicGroupMutationFn({ groupId }) {
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc('join_group', {
    p_group_id: groupId,
    p_invite_code: null,
  });
  if (error) throw error;
  const outcome = data?.outcome;
  if (outcome === 'joined') return { action: 'join_public', groupId };
  if (outcome === 'already_member') return { action: 'join_public', groupId, alreadyMember: true };
  const err = new Error(
    outcome === 'requested' || outcome === 'already_requested'
      ? 'Request sent - a group manager will let you in.'
      : outcome === 'full'
        ? 'This group is full.'
        : outcome === 'closed'
          ? 'Joining is closed for this event.'
          : 'Could not join the group.',
  );
  err.status = 403;
  throw err;
}

/**
 * Idempotent voluntary leave — replays safely when membership already left.
 */
export async function leaveGroupMutationFn({
  groupId,
  userId,
  membershipRowId,
  displayName,
  postSystemMessage = true,
}) {
  const supabase = getSupabase();

  let leavingRole = null;
  if (membershipRowId) {
    const { data: row } = await supabase
      .from('group_members')
      .select('id, left_at, role')
      .eq('id', membershipRowId)
      .maybeSingle();
    if (!row) {
      return { action: 'leave', groupId, alreadyLeft: true };
    }
    if (row.left_at) {
      return { action: 'leave', groupId, alreadyLeft: true };
    }
    leavingRole = row.role;
    const { error } = await supabase
      .from('group_members')
      .update({ left_at: new Date().toISOString() })
      .eq('id', membershipRowId);
    if (error) throw error;
  } else {
    const { leavingRole: role, error } = await insertVoluntaryLeaveRow(supabase, groupId, userId);
    if (error) {
      if (error.message === 'No active membership') {
        return { action: 'leave', groupId, alreadyLeft: true };
      }
      throw error;
    }
    leavingRole = role;
  }

  if (postSystemMessage) {
    const name = displayName || 'Someone';
    await supabase.from('messages').insert({
      group_id: groupId,
      user_id: null,
      message_type: 'system',
      conversation_type: 'group',
      content: `${name} left the group.`,
    });
  }

  // Creator handoff and group archiving are done by the
  // trg_cleanup_group_when_last_member_leaves trigger, which fires on the
  // left_at update above. Doing it here too produced duplicate notifications.

  return { action: 'leave', groupId, leavingRole };
}

export async function groupMembershipMutationFn(variables) {
  const { action } = variables;
  switch (action) {
    case 'join_public':
      return joinPublicGroupMutationFn(variables);
    case 'leave':
      return leaveGroupMutationFn(variables);
    default:
      throw new Error(`Unknown group membership action: ${action}`);
  }
}
